import type { Chip } from '@fantasy/types';

/**
 * Every term `Term.tsx` can attach a definition to (T-120). One vocabulary,
 * one wording, in one place — a component reaches for a key here rather
 * than writing its own paraphrase inline, so "blank" means the same thing
 * wherever a manager meets it. The four chip keys are `Chip` itself, not a
 * parallel set of names, so a suggestion or exclusion can hand its own
 * `chip` straight to `<Term>` with no lookup table in between.
 */
export type GlossaryKey =
  | Chip
  | 'blank'
  | 'double'
  | 'blankRun'
  | 'hit'
  | 'differential'
  | 'template'
  | 'effectiveOwnership'
  | 'teamValue';

/**
 * One sentence per term, in the app's own voice — plain enough for a first
 * season, without dropping the word itself (a manager five seasons in reads
 * "double gameweek" faster than a paraphrase of it). `hit` and
 * `effectiveOwnership` have no `<Term>` call site inside this ticket's
 * files — see the ChipCalendar/ChipStatus/RivalDifferentials/LeagueInsights
 * handover note for exactly where each belongs.
 */
export const GLOSSARY: Record<GlossaryKey, string> = {
  blank: "A blank is a gameweek where one of your players' club has no fixture at all, so they can't score you any points that week.",
  double: "A double is a gameweek where one of your players' club plays twice, giving you two chances at points instead of one.",
  blankRun: 'A blank run is several blank gameweeks for your squad landing close together — the kind of stretch worth planning a chip around.',
  hit: 'A hit is the 4-point penalty for making a transfer beyond your free ones — it comes straight off your gameweek score.',
  differential: 'A differential is a player few of your rivals own, so picking them can swing your rank when they come good.',
  template: "The template is the set of players enough managers own that everyone else's squad gets judged against it.",
  effectiveOwnership:
    'Effective ownership counts a captain twice, because a captain scores twice — so it adds ownership and captaincy together and can go past 100%. Everyone owning a player and half of them captaining him is 150%: the amount of his score you are up against.',
  teamValue:
    "Squad value plus bank naturally grows past £100.0m as your players' prices rise — that's profit, not a mistake. Reported twice as a bug before we added this note: your squad's original £100.0m only ever bounded what you could spend when you bought it.",
  wildcard: 'The Wildcard lets you rebuild your whole squad in one gameweek without paying any transfer hits.',
  freehit: 'The Free Hit lets you swap your whole squad for a single gameweek only — it reverts back straight after.',
  bboost: "Bench Boost counts your bench's points too, for one gameweek.",
  '3xc': "Triple Captain triples your captain's points that gameweek, instead of the usual double.",
};
